/**
 * The page's ad server connection — Atlas ch.4 B3 to B10, for the units
 * `boot()` renders.
 *
 * `boot()` mounts each GAM-demand unit (sticky banner, native, corner slider,
 * outstream video) into its own container and registers one GPT slot per
 * placement here. Everything registered in the same tick runs as ONE auction
 * and ONE batched GAM request, which is what lets GAM apply roadblocks and
 * competitive exclusions across the page. A unit mounted later (a delay, a
 * scroll trigger) gets its own auction for its slot only.
 *
 * What this owns, so the mount code does not have to:
 *   - loading GPT and Prebid (reusing the publisher's own copies when present),
 *   - configuring Prebid from `sdk-config.auction` — the SAME price buckets the
 *     GAM line items were built on, or bids land in buckets nothing matches,
 *   - defining the GPT slots,
 *   - telling each slot what GAM answered and which Prebid bid won.
 *
 * Never throws into the page. A missing GPT collapses the slots; a missing
 * Prebid still asks GAM (AdX and direct-sold compete there without it).
 */
import { AuctionDriver } from './AuctionDriver.js';
import { resolveConsent } from './ConsentGate.js';

export const GPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

/**
 * The Prebid build Playbid hosts, relative to the Playbid origin (the one that
 * serves /v1/sdk-config): Prebid 11.36.0 with exactly the adapters in the
 * platform's SSP catalogue plus the consent (TCF, GPP, USP) and floors modules,
 * 346 KB. Served from our own CDN and pinned by name, never a public package
 * CDN or `latest`: it runs on every publisher page we serve, so a Prebid
 * upgrade has to be a deliberate change here. Rebuilt and checked on the
 * platform side (docs/engineering/prebid-bundle.md).
 */
export const PREBID_BUNDLE_PATH = 'prebid/prebid-11.36.0.js';

/** How long GPT / Prebid may take to load before the page gives up on them. */
export const STACK_TIMEOUT_MS = 5000;

/** How long a refresh waits for GAM to answer the refreshed slot before it
 *  counts as failed (the slot keeps its creative; RefreshController retries). */
export const REFRESH_ANSWER_TIMEOUT_MS = 10_000;

/** Prebid's auction timeout when the config sets none. */
export const DEFAULT_AUCTION_TIMEOUT_MS = 1500;

const swallow = (fn, onError) => {
  try {
    return fn();
  } catch (err) {
    onError?.(err);
    return undefined;
  }
};

/** Inject one async script. Resolves true on load, false on error — never rejects. */
export function loadScript(doc, src) {
  return new Promise((resolve) => {
    try {
      const el = doc.createElement('script');
      el.async = true;
      el.src = src;
      el.onload = () => resolve(true);
      el.onerror = () => resolve(false);
      (doc.head || doc.documentElement).appendChild(el);
    } catch {
      resolve(false);
    }
  });
}

function scriptPresent(doc, fragment) {
  try {
    return Array.from(doc.scripts ?? []).some((s) => (s.src || '').includes(fragment));
  } catch {
    return false;
  }
}

let requestSeq = 0;
function makeRequestId(slot) {
  requestSeq += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `${slot}-${Date.now().toString(36)}-${requestSeq}-${rand}`;
}

/** Prebid's `setConfig` for this site, straight from `sdk-config.auction`. */
export function prebidConfigFor(config, { timeoutMs } = {}) {
  const a = config?.auction ?? {};
  const out = {
    bidderTimeout: timeoutMs ?? DEFAULT_AUCTION_TIMEOUT_MS,
    enableSendAllBids: a.enableSendAllBids !== false,
  };
  if (a.priceGranularity) out.priceGranularity = a.priceGranularity;
  // Outstream video bids are bucketed on the VIDEO ladder, the one the video
  // line items were built on (Atlas ch.3).
  if (a.videoPriceGranularity) out.mediaTypePriceGranularity = { video: a.videoPriceGranularity };
  if (a.targetingControls) out.targetingControls = a.targetingControls;
  return out;
}

/**
 * @typedef {object} SlotRegistration
 * @property {string} slot          The unit's key (`banner.top`, `native`, ...) — the reporting grain.
 * @property {string} elementId     The GPT element id / Prebid ad unit code. Unique on the page.
 * @property {string} adUnitPath    `gam.adUnitPath` from sdk-config.
 * @property {number[][]} sizes     Sizes to request, already narrowed to this device.
 * @property {'banner'|'video'} [mediaType]
 * @property {object} [video]       sdk-config's `unit.video` spec (outstream).
 * @property {Array<{bidder: string, params: object}>} [bidders]
 * @property {number} [floorCpm]
 * @property {string} [rendererUrl] Required by Prebid for a video renderer; never loaded.
 * @property {(bid: object) => void} [renderVideo]  Plays a Prebid video win.
 * @property {(r: {isEmpty: boolean, size?: number[], lineItemId?: unknown, creativeId?: unknown, advertiserId?: unknown, reason?: string}) => void} [onRender]
 * @property {(bid: object) => void} [onBidWon]
 */

export class PageAdServer {
  #config;
  #win;
  #doc;
  #prebidSrc;
  #gptSrc;
  #sendBids;
  #onDiagnostic;
  #loadScript;
  #setTimeoutFn;
  #now;
  #timeoutMs;
  #slots = new Map();
  #pending = [];
  #flushScheduled = false;
  #stackPromise = null;
  #consentPromise = null;
  #pageInitialLoad = false;

  /**
   * @param {object} opts
   * @param {object} opts.config        The sdk-config payload.
   * @param {Window} [opts.win]
   * @param {string} [opts.prebidSrc]
   * @param {string} [opts.gptSrc]
   * @param {(batch: object) => void} [opts.sendBids]
   * @param {(msg: string, detail?: unknown) => void} [opts.onDiagnostic]
   */
  constructor({
    config,
    win = typeof window !== 'undefined' ? window : undefined,
    prebidSrc,
    gptSrc,
    sendBids,
    onDiagnostic,
    loadScriptFn,
    setTimeoutFn,
    now = () => Date.now(),
  } = {}) {
    this.#config = config ?? {};
    this.#win = win;
    this.#doc = win?.document;
    // No source means no Prebid: the page's own copy if it has one, otherwise
    // GAM decides alone (AdX and direct-sold still compete there).
    this.#prebidSrc = prebidSrc || null;
    this.#gptSrc = gptSrc || GPT_SRC;
    this.#sendBids = sendBids ?? (() => {});
    this.#onDiagnostic = onDiagnostic ?? (() => {});
    this.#loadScript = loadScriptFn ?? ((src) => loadScript(this.#doc, src));
    this.#setTimeoutFn = setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
    this.#now = now;
    const t = Number(this.#config?.auction?.timeoutMs);
    this.#timeoutMs = Number.isFinite(t) && t > 0 ? t : DEFAULT_AUCTION_TIMEOUT_MS;
  }

  /** Number of slots registered so far (diagnostics and tests). */
  get size() {
    return this.#slots.size;
  }

  /**
   * Register one GPT slot. Its auction starts on the next tick, together with
   * everything else registered in this tick.
   *
   * @param {SlotRegistration} reg
   */
  register(reg) {
    const fail = (reason) => {
      this.#onDiagnostic('slot not registered', { slot: reg?.slot, reason });
      swallow(() => reg?.onRender?.({ isEmpty: true, reason }));
    };
    if (!reg?.elementId || !reg?.adUnitPath) return fail('no GAM ad unit');
    if (!Array.isArray(reg.sizes) || reg.sizes.length === 0) return fail('no sizes for this device');
    if (this.#slots.has(reg.elementId)) return fail('element id already registered');
    const entry = { ...reg, requestId: makeRequestId(reg.slot) };
    this.#slots.set(reg.elementId, entry);
    this.#pending.push(entry);
    if (!this.#flushScheduled) {
      this.#flushScheduled = true;
      this.#setTimeoutFn(() => {
        this.#flushScheduled = false;
        this.#flush().catch((e) => this.#onDiagnostic('auction failed', String(e)));
      }, 0);
    }
  }

  async #flush() {
    const batch = this.#pending.splice(0);
    if (batch.length === 0) return;
    const { googletag, pbjs } = await this.#stack();
    if (!googletag) {
      for (const r of batch) swallow(() => r.onRender?.({ isEmpty: true, reason: 'GPT unavailable' }));
      return;
    }
    const consent = await this.#consent();

    const defined = await new Promise((resolve) => {
      const run = () => {
        const ok = [];
        for (const r of batch) {
          const slot = swallow(() =>
            googletag.defineSlot(r.adUnitPath, r.sizes, r.elementId)?.addService(googletag.pubads()),
          );
          if (!slot) {
            swallow(() => r.onRender?.({ isEmpty: true, reason: 'GPT refused the slot' }));
            continue;
          }
          // With initial load disabled (ours, or the page's), display() only
          // registers the slot; the request is the batched refresh after the
          // auction. A page that left initial load ON would fire a request here
          // without our key-values, so the call is deferred to the refresh.
          if (!this.#pageInitialLoad) swallow(() => googletag.display(r.elementId));
          r.defined = true;
          ok.push(r);
        }
        resolve(ok);
      };
      swallow(() => googletag.cmd.push(run), () => resolve([]));
    });
    if (defined.length === 0) return;

    const driver = new AuctionDriver({
      pbjs,
      googletag,
      sendBids: this.#sendBids,
      onError: (m, e) => this.#onDiagnostic(`auction: ${m}`, e),
      now: this.#now,
      setTimeoutFn: this.#setTimeoutFn,
      requestAds: this.#pageInitialLoad
        ? (slots) => {
            for (const s of slots) swallow(() => googletag.display(s.getSlotElementId()));
          }
        : undefined,
    });
    await driver.run({
      units: defined.map((r) => this.#auctionUnit(r)),
      consent,
      timeoutMs: this.#timeoutMs,
      samplingRate: this.#config?.telemetry?.samplingRate ?? 1,
    });
  }

  /** The AuctionDriver unit for one registered slot. */
  #auctionUnit(r) {
    return {
      slot: r.slot,
      requestId: r.requestId,
      divId: r.elementId,
      adUnitPath: r.adUnitPath,
      sizes: r.sizes,
      bidders: r.bidders ?? [],
      mediaType: r.mediaType,
      video: r.video,
      floorCpm: r.floorCpm,
      renderer:
        r.mediaType === 'video' && typeof r.renderVideo === 'function'
          ? {
              url: r.rendererUrl || this.#gptSrc,
              render: (bid) => swallow(() => r.renderVideo(bid), (e) => this.#onDiagnostic('video render failed', String(e))),
            }
          : undefined,
    };
  }

  /**
   * Refresh ONE slot that GAM already answered (Atlas ch.8 R6): a new Prebid
   * auction for that ad unit only, with a new request id (so its `/v1/bids`
   * batch is its own), targeting set for that slot only, then
   * `pubads().refresh([thatSlot])` - never the page's other slots.
   *
   * Resolves `{ isEmpty }` once GAM has answered the refreshed slot (after the
   * slot's `onRender` has run). Rejects when the slot is unknown, a refresh of
   * it is still in flight (R9), GPT is gone, or GAM does not answer within
   * REFRESH_ANSWER_TIMEOUT_MS. Never throws synchronously.
   *
   * @param {string} elementId
   * @returns {Promise<{isEmpty: boolean}>}
   */
  refresh(elementId) {
    const r = this.#slots.get(elementId);
    if (!r?.defined) return Promise.reject(new Error('slot not defined'));
    if (r.refreshing) return Promise.reject(new Error('auction in flight'));
    r.refreshing = true;
    r.requestId = makeRequestId(r.slot);
    return new Promise((resolve, reject) => {
      let done = false;
      const finish = (fn, value) => {
        if (done) return;
        done = true;
        r.refreshing = false;
        r.onAnswered = null;
        fn(value);
      };
      r.onAnswered = (answer) => finish(resolve, answer);
      this.#setTimeoutFn(() => finish(reject, new Error('no GAM answer')), REFRESH_ANSWER_TIMEOUT_MS);
      this.#runRefresh(r).catch((e) => finish(reject, e));
    });
  }

  async #runRefresh(r) {
    const { googletag, pbjs } = await this.#stack();
    if (!googletag) throw new Error('GPT unavailable');
    const consent = await this.#consent();
    // Always a GPT refresh of this one slot, even on a page that left initial
    // load on: display() of an already-displayed slot requests nothing.
    const driver = new AuctionDriver({
      pbjs,
      googletag,
      sendBids: this.#sendBids,
      onError: (m, e) => this.#onDiagnostic(`refresh auction: ${m}`, e),
      now: this.#now,
      setTimeoutFn: this.#setTimeoutFn,
    });
    await driver.run({
      units: [this.#auctionUnit(r)],
      consent,
      timeoutMs: this.#timeoutMs,
      samplingRate: this.#config?.telemetry?.samplingRate ?? 1,
    });
  }

  #consent() {
    if (!this.#consentPromise) {
      this.#consentPromise = Promise.resolve(
        swallow(() =>
          resolveConsent({
            mode: this.#config?.consent?.mode ?? 'required',
            timeoutMs: this.#config?.consent?.timeoutMs,
            win: this.#win,
          }),
        ),
      ).catch(() => undefined);
    }
    return this.#consentPromise;
  }

  /** Load and configure GPT and Prebid once per page. */
  #stack() {
    if (!this.#stackPromise) this.#stackPromise = this.#loadStack();
    return this.#stackPromise;
  }

  async #loadStack() {
    const win = this.#win;
    if (!win || !this.#doc) return {};
    const [googletag, pbjs] = await Promise.all([this.#loadGpt(), this.#loadPrebid()]);
    return { googletag, pbjs };
  }

  #withTimeout(promise) {
    return new Promise((resolve) => {
      let done = false;
      this.#setTimeoutFn(() => {
        if (!done) {
          done = true;
          resolve(null);
        }
      }, STACK_TIMEOUT_MS);
      promise.then(
        (v) => {
          if (!done) {
            done = true;
            resolve(v);
          }
        },
        () => {
          if (!done) {
            done = true;
            resolve(null);
          }
        },
      );
    });
  }

  #loadGpt() {
    const win = this.#win;
    win.googletag = win.googletag || { cmd: [] };
    win.googletag.cmd = win.googletag.cmd || [];
    if (!win.googletag.apiReady && !scriptPresent(this.#doc, 'gpt.js')) {
      this.#loadScript(this.#gptSrc).then((ok) => {
        if (!ok) this.#onDiagnostic('GPT failed to load', this.#gptSrc);
      });
    }
    const ready = new Promise((resolve) => {
      win.googletag.cmd.push(() => {
        const gt = win.googletag;
        const pubads = gt.pubads();
        // Ours to decide only if nobody enabled services yet. A page that runs
        // GPT itself keeps its own settings; we only add slots to it.
        if (!gt.pubadsReady) {
          swallow(() => pubads.disableInitialLoad());
          swallow(() => pubads.enableSingleRequest());
          swallow(() => gt.enableServices());
        }
        // True only on a page that enabled services with initial load ON:
        // there, display() itself is the request, so it waits for our targeting.
        this.#pageInitialLoad = swallow(() => pubads.isInitialLoadDisabled?.()) === false;
        swallow(() =>
          pubads.addEventListener('slotRenderEnded', (e) => {
            const id = swallow(() => e?.slot?.getSlotElementId?.());
            const r = this.#slots.get(id);
            if (!r) return;
            swallow(
              () =>
                r.onRender?.({
                  isEmpty: e?.isEmpty === true,
                  size: Array.isArray(e?.size) ? e.size : undefined,
                  lineItemId: e?.lineItemId ?? null,
                  creativeId: e?.creativeId ?? null,
                  advertiserId: e?.advertiserId ?? null,
                }),
              (err) => this.#onDiagnostic('onRender failed', String(err)),
            );
            // A refresh in flight for this slot is answered now (after the
            // mount has reacted to the answer).
            swallow(() => r.onAnswered?.({ isEmpty: e?.isEmpty === true }));
          }),
        );
        resolve(gt);
      });
    });
    return this.#withTimeout(ready);
  }

  #loadPrebid() {
    const win = this.#win;
    win.pbjs = win.pbjs || {};
    win.pbjs.que = win.pbjs.que || [];
    if (typeof win.pbjs.requestBids !== 'function' && !win.pbjs.libLoaded && this.#prebidSrc) {
      this.#loadScript(this.#prebidSrc).then((ok) => {
        if (!ok) this.#onDiagnostic('Prebid failed to load', this.#prebidSrc);
      });
    }
    const ready = new Promise((resolve) => {
      win.pbjs.que.push(() => {
        const pb = win.pbjs;
        swallow(() => pb.setConfig(prebidConfigFor(this.#config, { timeoutMs: this.#timeoutMs })));
        // A consent string only exists where a CMP does. Configuring Prebid's
        // consent module without one would only add its timeout to every
        // auction.
        const frameworks = this.#config?.consent?.frameworks ?? [];
        if (frameworks.includes('tcf') && typeof win.__tcfapi === 'function') {
          swallow(() =>
            pb.setConfig({
              consentManagement: { gdpr: { cmpApi: 'iab', timeout: this.#config?.consent?.timeoutMs ?? 1500 } },
            }),
          );
        }
        swallow(() =>
          pb.onEvent('bidWon', (bid) => {
            const r = this.#slots.get(bid?.adUnitCode);
            if (r) swallow(() => r.onBidWon?.(bid));
          }),
        );
        resolve(pb);
      });
    });
    return this.#withTimeout(ready);
  }
}
